import React, { useEffect, useRef, useState } from 'react';
import eiffelBlack from '../../assets/images/eiffel-black.jpg';
import eiffelWhite from '../../assets/images/eiffel-white.jpg';
import useImagePreload from '../../hooks/useImagePreload';
import BrandedLoader from '../BrandedLoader/BrandedLoader';
import CherryBlossoms from '../CherryBlossoms/CherryBlossoms';
import './Hero.css';

/**
 * Full-screen hero banner.
 *
 * Concept: House TAU's Eiffel Tower is normally black. During
 * feesjaar it gets painted gold, and white is the transitional
 * primer coat -- which is where the house is right now.
 *
 * Sequence on load:
 *   1. White tower shown for half a second, zoom already progressing.
 *   2. A rapid glitch burst: a few quick cuts between black and white.
 *   3. Settles back on white, continuing the same slow zoom.
 *
 * The zoom lives on a single wrapper around both images, so the two
 * photos always share the exact same scale/position -- that's what
 * keeps the swap looking like one tower flickering rather than two
 * different photos cutting between each other.
 *
 * The whole sequence only starts once both photos are confirmed loaded
 * (see useImagePreload) -- on a cold cache, starting the timer
 * immediately meant the glitch could run before the browser had
 * actually finished downloading the images, so it visually never
 * played at all on a first visit.
 */
const SEQUENCE = [
  { img: 'white', hold: 500, zoom: true },  // short beat so the white tower registers, then straight into the glitch
  { img: 'black', hold: 90, zoom: true },   // rapid glitch burst starts
  { img: 'white', hold: 80, zoom: true },
  { img: 'black', hold: 70, zoom: true },
  { img: 'white', hold: 80, zoom: true },
  { img: 'black', hold: 70, zoom: true },
  { img: 'white', hold: 90, zoom: true },
  { img: 'black', hold: 450, zoom: true },  // deliberate hold -- long enough to register
  { img: 'white', hold: 0, zoom: true, settle: true }, // finalises here, glitch "closes"
];

export default function Hero({ title = 'House TAU', subtitle = 'Iron Sharpens Iron · Est. 1963' }) {
  const imagesReady = useImagePreload([eiffelBlack, eiffelWhite]);
  const [step, setStep] = useState(0);
  const [showText, setShowText] = useState(false);
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (!imagesReady) return;

    let i = 0;

    const advance = () => {
      setStep(i);
      if (SEQUENCE[i].settle) {
        setShowText(true);
        return;
      }
      timeoutRef.current = setTimeout(() => {
        i += 1;
        advance();
      }, SEQUENCE[i].hold);
    };

    advance();
    return () => clearTimeout(timeoutRef.current);
  }, [imagesReady]);

  if (!imagesReady) {
    return (
      <section className="hero">
        <BrandedLoader fullscreen />
      </section>
    );
  }

  const { img: current, zoom: zooming, settle: settled } = SEQUENCE[step];

  return (
    <section className={`hero ${settled ? 'hero--settled' : ''}`}>
      <div className="hero__media">
        <div className={`hero__zoom-layer ${zooming ? 'is-zooming' : ''}`}>
          <img
            src={eiffelBlack}
            alt=""
            className={`hero__img ${current === 'black' ? 'is-active' : ''}`}
          />
          <img
            src={eiffelWhite}
            alt=""
            className={`hero__img ${current === 'white' ? 'is-active' : ''}`}
          />
        </div>
        <div className="hero__vignette" aria-hidden="true" />
        <CherryBlossoms />
      </div>

      <div className={`hero__content ${showText ? 'is-visible' : ''}`}>
        <p className="hero__eyebrow">
          <span className="hero__eyebrow-dot" />
          Feesjaar in progress: the coat is turning gold
        </p>
        <h1 className="hero__title">{title}</h1>
        <p className="hero__subtitle">{subtitle}</p>
        <div className="hero__cta-row">
          <a href="#about" className="hero__cta hero__cta--primary">Discover the House</a>
          <a href="/gallery" className="hero__cta hero__cta--ghost">View the Gallery</a>
        </div>
      </div>

      <div className="hero__scroll-hint" aria-hidden="true">
        <span />
      </div>
    </section>
  );
}
